import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import midasInstance from '@rsos/midas/src/midasInstance';
import { selectExistingRequest } from '@rsos/reduxDataStore';
import {
  setFormPageToShow,
  setIsNonCallSubmit,
  setPaneOpen,
} from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import type { PSAPInfo } from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import {
  useSelectedCallerID,
  useSelectedCall,
} from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import type { RootState } from '../../../../app';
import {
  trackSubmitAirAmbulanceRequest,
  trackSubmitAirAmbulanceRequestApiResponse,
} from '../../../../gainsightTrackings/airAmbulanceTrackings';
// import { notifySuccess } from '@rsos/utils/toastUtils';
import {
  FORM_PAGES,
  LAUNCH_TYPES,
  LANDING_ZONE_EXTERNAL_ID,
} from '../../AirAmbulanceForm/constants';
import { getSelectValue } from '../../AirAmbulanceForm/utils';
import { parseCoordinates } from '../../AirAmbulanceForm/validation';
import type {
  TentativeETA,
  FormValuesWithSelect,
  ExistingRequest,
} from '../../types';

// Payload interfaces for the API request
interface FromUnite {
  logged_in_fname: string;
  logged_in_lname: string;
  logged_in_email: string;
  ecc_id: string;
  caller_phone?: string;
}

interface TripDataPayload {
  location: {
    latitude: string;
    longitude: string;
  };
  pickup_details: string;
  trip_reason: string;
  ground_contact: string;
  pickup_location: string;
  patient_info: string;
  weather_decline: boolean;
  another_carrier: boolean;
  multiple_units_requested: boolean;
  launch_type: string;
  external_id?: string;
}

interface PayloadVariables {
  from_unite: FromUnite;
  trip_data: TripDataPayload;
  idenStandBy?: boolean;
  idenStandByActivated?: boolean;
  request_id?: string;
  ext_trip_id?: string;
  olos_trip_id?: string;
}

interface ApiPayload {
  callflow: string;
  variables: PayloadVariables;
}

const useFormSubmit = (
  isLoading: boolean,
  setIsLoading: (loading: boolean) => void,
  tentativeETA: TentativeETA | null,
  UUID: string,
  fromMapCard: boolean,
  isStandby = false, // this is true when the activate standby button is clicked
): ((values: FormValuesWithSelect) => Promise<null | undefined>) => {
  const dispatch = useDispatch();
  const currentPSAP = useSelector(
    (state: RootState) => state.psaps.currentPSAP as PSAPInfo | null,
  );
  const selectedCallerID = useSelectedCallerID();
  const selectedCall = useSelectedCall();
  const existingRequest = useSelector(
    selectExistingRequest,
  ) as ExistingRequest | null;

  // Check if we have a location from the selected call
  const hasLocation = selectedCall ? getMostRecentLoc(selectedCall) : null;

  return useCallback(
    async (values: FormValuesWithSelect) => {
      if (isLoading) return null;
      setIsLoading(true);

      dispatch(setIsNonCallSubmit(fromMapCard));
      dispatch(setPaneOpen(true));

      // Determine if this is an edit or new submission
      const isEditMode = !!existingRequest?.body;

      let requestId: string | null | undefined = null;

      // Use callerID as requestId if we have location and not in edit mode
      if (hasLocation && !isEditMode) {
        requestId = selectedCallerID;
      } else {
        requestId =
          existingRequest?.body?.ext_trip_id ||
          tentativeETA?.extSystemTripId ||
          existingRequest?.request_id;
      }

      trackSubmitAirAmbulanceRequest({
        requestId,
        requestType: values.launch_type,
        uuid: UUID,
        isEdit: isEditMode,
      });

      try {
        const coords = parseCoordinates(values.pickup_coordinates);

        // Determine the callflow based on whether we're in edit mode
        const callflow =
          isEditMode || isStandby
            ? '/air_ambulance/gmr_edit'
            : '/air_ambulance/gmr_air_request';

        const fromUnite: FromUnite = {
          logged_in_fname: currentPSAP?.contact_name || '',
          logged_in_lname: currentPSAP?.contact_name || '',
          logged_in_email: currentPSAP?.contact_email || '',
          ecc_id: currentPSAP?.account_id || '',
        };

        // Add caller_phone if we have location and not in edit mode
        if (hasLocation && !isEditMode && selectedCallerID) {
          fromUnite.caller_phone = `+${selectedCallerID}`;
        }

        const landingZoneExternalId = values[LANDING_ZONE_EXTERNAL_ID];

        const payload: ApiPayload = {
          callflow,
          variables: {
            from_unite: fromUnite,
            ...(isStandby && {
              idenStandBy: values.launch_type === LAUNCH_TYPES.STANDBY,
              idenStandByActivated: isStandby,
            }),
            trip_data: {
              location: {
                latitude: coords?.latitude?.toString() || '',
                longitude: coords?.longitude?.toString() || '',
              },
              pickup_details: values.pickup_details || '',
              trip_reason: getSelectValue(values.request_reason),
              ground_contact: values.ground_contact || '',
              pickup_location: values.pickup_location || '',
              patient_info: values.patient_info || '',
              weather_decline: values.air_service_declined === 'Yes',
              another_carrier: values.air_service_responding === 'Yes',
              multiple_units_requested:
                values.multiple_units_requested === 'Yes',
              launch_type: values.launch_type,
              // If a landing zone was selected, include its external_id
              ...(typeof landingZoneExternalId === 'string' &&
                landingZoneExternalId && {
                  external_id: landingZoneExternalId,
                }),
            },
          },
        };

        // For edit mode, include the request ID and trip IDs
        if (isEditMode && existingRequest?.body) {
          if (existingRequest?.body?.id) {
            payload.variables.request_id = existingRequest.body.id;
          }

          // Only include ext_trip_id and olos_trip_id in edit mode
          if (existingRequest?.body?.ext_trip_id) {
            payload.variables.ext_trip_id = existingRequest.body.ext_trip_id;
          }

          if (existingRequest?.body?.olos_trip_id) {
            payload.variables.olos_trip_id = existingRequest.body.olos_trip_id;
          }
        } else if (tentativeETA && tentativeETA.extSystemTripId) {
          payload.variables.ext_trip_id = tentativeETA.extSystemTripId;
        }

        const response = await midasInstance.post(
          '/internal_auth/trigger',
          payload,
          {
            headers: { retry: true },
          },
        );

        // TODO: Uncomment this if we need it later
        // notifySuccess({
        //   message: `Air Ambulance Request ${isEditMode ? 'Updated' : 'Submitted'}`,
        // });
        trackSubmitAirAmbulanceRequestApiResponse({
          requestId,
          status: response?.status,
          error: false,
          success: response?.data?.detail,
          UUID,
          isEdit: isEditMode,
        });
      } catch (error) {
        const err = error as {
          response?: { status?: number };
          message?: string;
        };
        trackSubmitAirAmbulanceRequestApiResponse({
          requestId,
          status: err?.response?.status,
          error: err?.message || false,
          success: false,
          UUID,
          isEdit: isEditMode,
        });
      } finally {
        dispatch(setFormPageToShow(FORM_PAGES.STATUS));
        // setIsLoading(false);
      }
    },
    [
      selectedCallerID,
      hasLocation,
      currentPSAP,
      existingRequest,
      setIsLoading,
      tentativeETA,
      UUID,
      dispatch,
      fromMapCard,
      isLoading,
      isStandby,
    ],
  );
};

export default useFormSubmit;
